import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { filterRowsForCurrentScope, getCenterScopedUrl } from "../../utils/centerScope";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";

/* ===== COLUMN DEFINITION (SINGLE SOURCE OF TRUTH) ===== */
const COURSE_FIELDS = [
    { key: "courseId", label: "Course ID", readonly: true },
    // { key: "subSchemeName", label: "Sub Scheme Name" },
    { key: "courseType", label: "Course Type" },
    { key: "sector", label: "Sector" },
    { key: "jobRoleMappingType", label: "Job Role Mapping Type" },
    { key: "qpCode", label: "QP Code" },
    { key: "courseName", label: "Course Name" },
    { key: "nsqfLevel", label: "NSQF Level" },
    { key: "minimumAge", label: "Minimum Age" },
    { key: "minimumEducationRequired", label: "Minimum Education Required" },
    { key: "courseDurationHours", label: "Course Duration (Hours)" },
    { key: "hoursPerDay", label: "Hours Per Day" },
    // { key: "courseFee", label: "Course Fee" },
    { key: "courseValidity", label: "Course Validity" },
    // { key: "departmentName", label: "Department" },
    // { key: "projectName", label: "Project Name" },
    // { key: "schemesProgramsName", label: "Schemes / Programs" },
];

export default function CourseManagementPage() {
    const token = localStorage.getItem("token");
    const store = useDataStore();
    const CACHE_KEY = "courses";

    /* ================= COMMON ================= */
    const [msg, setMsg] = useState({ type: "", text: "" });
    const flash = (type, text) => {
        setMsg({ type, text });
    };

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    const jsonHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        }),
        [token]
    );

    /* ================= EXCEL UPLOAD ================= */
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    const uploadExcel = async () => {
        if (!file) return flash("err", "Please select Excel file");
        let headerRow;
        let dataRows;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            headerRow = raw[0];
            dataRows = raw.slice(1);
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }
        if (!dataRows.length) return flash("err", "Excel file is empty");
        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
        let uploadedSoFar = 0;
        let totalInserted = 0;
        let totalSkipped = 0;
        let failed = false;
        let firstError = "";
        const uploadOneChunk = async (i) => {
            const chunkRows = [headerRow, ...dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)];
            const res = await fetch(`${BASE_URL}/api/courses/upload-json?actor=Rahul`, {
                method: "POST",
                headers: { ...authHeaders, "Content-Type": "application/json" },
                body: JSON.stringify(chunkRows),
            });
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const text = await res.text();
            let data;
            try { data = JSON.parse(text); } catch { data = text; }
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
            return { inserted: data?.inserted ?? 0, skipped: data?.skipped ?? 0 };
        };
        for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
            const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
            for (const result of results) {
                if (result.status === "fulfilled") {
                    totalInserted += result.value.inserted;
                    totalSkipped += result.value.skipped;
                    uploadedSoFar += result.value.inserted + result.value.skipped;
                } else {
                    failed = true;
                    firstError = result.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
        }
        setUploading(false);
        if (!failed) {
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchAllCourses();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    /* ================= TABLE ================= */
    const [courses, setCourses] = useState(() => store.getRows(CACHE_KEY));
    const [loading, setLoading] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [totalCount, setTotalCount] = useState(null);
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);

    const fetchFirst100 = async () => {
        if (store.isCached(CACHE_KEY)) {
            setCourses(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);
        try {
            const scopedUrl = getCenterScopedUrl(BASE_URL, "/api/courses");
            const res = await fetch(scopedUrl + (scopedUrl.includes("?") ? "&" : "?") + "page=0&size=100", { headers: authHeaders });
            const data = await res.json();
            const all = Array.isArray(data) ? data : [];
            setCourses(all);
            setAllLoaded(false);
            store.setRows(CACHE_KEY, all, false);
        } catch {
            setCourses([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllCourses = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/courses"), { headers: authHeaders });
            const data = await res.json();
            const all = Array.isArray(data) ? data : [];
            setCourses(all);
            setAllLoaded(true);
            setVisibleCount(all.length);
            store.markAllLoaded(CACHE_KEY, all);
        } catch {
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => {
        fetchFirst100();
    }, []);

    useEffect(() => {
        fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setTotalCount(d.courses ?? null); })
            .catch(() => { });
    }, []);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return filterRowsForCurrentScope(courses).filter((c) =>
            COURSE_FIELDS.some((f) =>
                String(c[f.key] ?? "").toLowerCase().includes(q)
            )
        );
    }, [courses, search]);

    const [exporting, setExporting] = useState(false);

    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let data = courses;
            if (!allLoaded) {
                const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/courses"), { headers: authHeaders });
                const json = await res.json();
                data = Array.isArray(json) ? json : [];
                setCourses(data);
                setAllLoaded(true);
                setVisibleCount(data.length);
                store.markAllLoaded(CACHE_KEY, data);
            }
            if (!data.length) return flash("err", "No data to export");
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Courses");
            XLSX.writeFile(wb, "courses.xlsx");
            flash("ok", `Exported ${data.length.toLocaleString()} records`);
        } catch (e) {
            flash("err", "Export failed: " + e.message);
        } finally {
            setExporting(false);
        }
    };

    /* ================= EDIT ================= */
    const emptyForm = useMemo(() => {
        const o = {};
        COURSE_FIELDS.forEach((f) => (o[f.key] = ""));
        return o;
    }, []);

    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const openEdit = (row) => {
        setEditingId(row.courseId);
        const filled = {};
        COURSE_FIELDS.forEach((f) => {
            filled[f.key] = row[f.key] ?? "";
        });
        setForm(filled);
    };

    const updateCourse = async () => {
        try {
            const payload = { ...form };
            delete payload.courseId;

            await fetch(
                `${BASE_URL}/api/courses?courseId=${encodeURIComponent(editingId)}`,
                {
                    method: "PUT",
                    headers: jsonHeaders,
                    body: JSON.stringify(payload),
                }
            );
            flash("ok", "Course updated");
            setEditingId(null);
            store.invalidate(CACHE_KEY);
            fetchAllCourses();
        } catch {
            flash("err", "Update failed");
        }
    };

    /* ================= DELETE ================= */
    const deleteCourse = async (courseId) => {
        if (!window.confirm("Deactivate this course?")) return;

        await fetch(
            `${BASE_URL}/api/courses?courseId=${encodeURIComponent(courseId)}`,
            {
                method: "DELETE",
                headers: authHeaders,
            }
        );
        flash("ok", "Course deactivated");
        store.invalidate(CACHE_KEY);
        fetchAllCourses();
    };

    /* ================= AUDIT ================= */
    const [auditOpen, setAuditOpen] = useState(false);
    const [auditRows, setAuditRows] = useState([]);

    const openAudit = async (courseId) => {
        setAuditOpen(true);
        const res = await fetch(
            `${BASE_URL}/api/courses/audits/by-course?courseId=${encodeURIComponent(
                courseId
            )}`,
            { headers: authHeaders }
        );
        const data = await res.json();
        setAuditRows(Array.isArray(data) ? data : []);
    };


    const downloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/templates/course_creation_template.xlsx"; // local path
        a.download = "course_creation_template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    /* ================= UI ================= */
    return (
        <div className="cmp-page">
            <h1>Course Management</h1>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {uploadProgress && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
                            {uploadProgress.error
                                ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
                                : uploading
                                    ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
                                    : `Upload complete`}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                            {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
                            &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
                        </span>
                    </div>
                    <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
                            background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
                            borderRadius: 999,
                            transition: "width 0.3s ease",
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                        {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
                    </div>
                </div>
            )}

            {/* Upload */}
            <div className="card grid-3">
                <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }} />
                <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
                    {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
                </button>
                {/* <button className="btn" onClick={exportExcel}>Export Excel</button> */}
                <button className="btn btn-outline" onClick={downloadTemplate}>
                    Download Excel Template
                </button>
            </div>

            {/* Table */}
            <div className="card">

                <div className="card-top">
                    <div className="card-title" style={{ fontWeight: "800" }}>2️⃣ Course Table</div>

                    <div className="table-actions" style={{ marginTop: "1rem" }}>
                        <input
                            className="search"
                            placeholder="Search..."
                            style={{ marginRight: "1rem" }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <button className="btn" onClick={exportExcel} disabled={exporting}>{exporting ? "Processing..." : "Export Excel"}</button>
                    </div>
                </div>
                {totalCount !== null && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
                        Total: {totalCount.toLocaleString()}
                    </div>
                )}




                <div className="table-wrap">
                    <table className="ep-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                {COURSE_FIELDS.map((f) => (
                                    <th key={f.key}>{f.label}</th>
                                ))}
                                <th>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filtered.slice(0, visibleCount).map((c, i) => (
                                <tr key={c.courseId}>
                                    <td>{i + 1}</td>
                                    {COURSE_FIELDS.map((f) => (
                                        <td key={f.key}>{c[f.key]}</td>
                                    ))}
                                    <td className="actions">
                                        {/* <button className="btn btn-sm" onClick={() => openEdit(c)}>Edit</button> */}
                                        {/* <button className="btn btn-sm" onClick={() => openAudit(c.courseId)}>Audit</button> */}
                                        <button className="btn btn-sm btn-danger" onClick={() => deleteCourse(c.courseId)}>Deactivate</button>
                                    </td>
                                </tr>
                            ))}

                            {!filtered.length && (
                                <tr>
                                    <td colSpan={COURSE_FIELDS.length + 2} className="muted">
                                        {loading ? "Loading..." : "No courses found"}
                                    </td>
                                </tr>
                            )}
                            {!allLoaded && !loading && (
                                <tr>
                                    <td colSpan={COURSE_FIELDS.length + 2} style={{ textAlign: "center", padding: "14px" }}>
                                        <button className="btn btn-outline" onClick={fetchAllCourses} disabled={loadingAll}>
                                            {loadingAll ? "Loading all data…" : "Load All Data"}
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {allLoaded && filtered.length > visibleCount && (
                                <tr>
                                    <td colSpan={COURSE_FIELDS.length + 2} style={{ textAlign: "center", padding: "14px" }}>
                                        <button className="btn btn-outline" onClick={() => setVisibleCount(c => c + 100)}>
                                            Show More ({filtered.length - visibleCount} remaining)
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* EDIT MODAL */}
            {editingId &&
                createPortal(
                    <div className="modal-overlay" onClick={() => setEditingId(null)}>
                        <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
                            <h2>Edit Course</h2>

                            {COURSE_FIELDS.map(
                                (f) =>
                                    !f.readonly && (
                                        <input
                                            key={f.key}
                                            placeholder={f.label}
                                            value={form[f.key]}
                                            onChange={(e) =>
                                                setForm((p) => ({ ...p, [f.key]: e.target.value }))
                                            }
                                        />
                                    )
                            )}

                            <div className="modal-actions">
                                <button className="btn" onClick={() => setEditingId(null)}>Cancel</button>
                                <button className="btn btn-primary" onClick={updateCourse}>Save</button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

            {/* AUDIT MODAL */}
            {auditOpen &&
                createPortal(
                    <div className="modal-overlay" onClick={() => setAuditOpen(false)}>
                        <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
                            <h2>Course Audit Logs</h2>
                            <pre>{JSON.stringify(auditRows, null, 2)}</pre>
                        </div>
                    </div>,
                    document.body
                )}

            {/* INLINE CSS */}
            <style>{`
        .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
        h1 { font-size:26px; font-weight:900 }

        .flash { padding:10px; border-radius:10px; margin:10px 0; font-weight:700 }
        .flash-ok { background:#ecfdf5; color:#065f46 }
        .flash-err { background:#fef2f2; color:#991b1b }

        .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
        .grid-3 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

        .btn { border:1px solid #e5e7eb; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer; background:#fff }
        .btn-primary { background:#2563eb; color:#fff; border-color:#2563eb }
        .btn-sm { padding:6px 10px; font-size:12px }
        .btn-danger { color:#991b1b; border-color:#fca5a5 }

        .search { margin-bottom:10px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

        .table-wrap { width:100%; overflow-x:auto }
        .ep-table { width:max-content; min-width:1600px; border-collapse:collapse }
        th, td { padding:10px 12px; border-bottom:1px solid #e5e7eb; font-size:13px; white-space:nowrap }
        th { background:#fcfcff; color:#6b7280; text-align:left }

        .actions { display:flex; gap:6px }
        .muted { color:#6b7280; font-weight:700 }

        .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
        .ep-modal { background:#fff; padding:18px; border-radius:16px; width:600px; max-height:90vh; overflow:auto }
        .ep-modal.wide { width:900px }
        .ep-modal input { width:100%; margin-bottom:8px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

        .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px }
      `}</style>
        </div>
    );
}




// import React, { useEffect, useMemo, useState } from "react";
// import { createPortal } from "react-dom";
// import * as XLSX from "xlsx";

// const BASE_URL = "https://mis.empowerplus.in";

// export default function CourseManagementPage() {
//     const token = localStorage.getItem("token");

//     /* ================= COMMON ================= */
//     const [msg, setMsg] = useState({ type: "", text: "" });
//     const flash = (type, text) => {
//         setMsg({ type, text });
//         setTimeout(() => setMsg({ type: "", text: "" }), 3000);
//     };

//     const authHeaders = useMemo(
//         () => ({ Authorization: `Bearer ${token}` }),
//         [token]
//     );

//     const jsonHeaders = useMemo(
//         () => ({
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//         }),
//         [token]
//     );

//     /* ================= EXCEL UPLOAD ================= */
//     const [file, setFile] = useState(null);
//     const [uploading, setUploading] = useState(false);

//     const uploadExcel = async () => {
//         if (!file) return flash("err", "Please select Excel file");

//         const fd = new FormData();
//         fd.append("file", file);

//         setUploading(true);
//         try {
//             await fetch(`${BASE_URL}/api/courses/upload?actor=Rahul`, {
//                 method: "POST",
//                 headers: authHeaders,
//                 body: fd,
//             });
//             flash("ok", "Excel uploaded successfully");
//             setFile(null);
//             fetchCourses();
//         } catch {
//             flash("err", "Upload failed");
//         } finally {
//             setUploading(false);
//         }
//     };

//     /* ================= TABLE ================= */
//     const [courses, setCourses] = useState([]);
//     const [loading, setLoading] = useState(false);
//     const [search, setSearch] = useState("");

//     const fetchCourses = async () => {
//         setLoading(true);
//         try {
//             const res = await fetch(`${BASE_URL}/api/courses`, {
//                 headers: authHeaders,
//             });
//             const data = await res.json();
//             setCourses(Array.isArray(data) ? data : []);
//         } catch {
//             setCourses([]);
//         } finally {
//             setLoading(false);
//         }
//     };

//     useEffect(() => {
//         fetchCourses();
//     }, []);

//     const filtered = useMemo(() => {
//         const q = search.toLowerCase();
//         return courses.filter(
//             (c) =>
//                 c.courseId?.toLowerCase().includes(q) ||
//                 c.courseName?.toLowerCase().includes(q) ||
//                 c.projectName?.toLowerCase().includes(q) ||
//                 c.departmentName?.toLowerCase().includes(q)
//         );
//     }, [courses, search]);

//     const exportExcel = () => {
//         const ws = XLSX.utils.json_to_sheet(filtered);
//         const wb = XLSX.utils.book_new();
//         XLSX.utils.book_append_sheet(wb, ws, "Courses");
//         XLSX.writeFile(wb, "courses.xlsx");
//     };

//     /* ================= EDIT ================= */
//     const emptyForm = {
//         courseName: "",
//         departmentName: "",
//         projectName: "",
//         sector: "",
//         qpCode: "",
//         nsqfLevel: "",
//         courseDurationHours: "",
//     };

//     const [editingId, setEditingId] = useState(null);
//     const [form, setForm] = useState(emptyForm);

//     const openEdit = (row) => {
//         setEditingId(row.courseId);
//         setForm({
//             courseName: row.courseName ?? "",
//             departmentName: row.departmentName ?? "",
//             projectName: row.projectName ?? "",
//             sector: row.sector ?? "",
//             qpCode: row.qpCode ?? "",
//             nsqfLevel: row.nsqfLevel ?? "",
//             courseDurationHours: row.courseDurationHours ?? "",
//         });
//     };

//     const updateCourse = async () => {
//         try {
//             await fetch(
//                 `${BASE_URL}/courses?courseId=${encodeURIComponent(editingId)}`,
//                 {
//                     method: "PUT",
//                     headers: jsonHeaders,
//                     body: JSON.stringify(form),
//                 }
//             );
//             flash("ok", "Course updated");
//             setEditingId(null);
//             fetchCourses();
//         } catch {
//             flash("err", "Update failed");
//         }
//     };

//     /* ================= DELETE ================= */
//     const deleteCourse = async (courseId) => {
//         if (!window.confirm("Deactivate this course?")) return;

//         await fetch(
//             `${BASE_URL}/courses?courseId=${encodeURIComponent(courseId)}`,
//             {
//                 method: "DELETE",
//                 headers: authHeaders,
//             }
//         );
//         flash("ok", "Course deactivated");
//         fetchCourses();
//     };

//     /* ================= AUDIT ================= */
//     const [auditOpen, setAuditOpen] = useState(false);
//     const [auditRows, setAuditRows] = useState([]);
//     const [auditCourseId, setAuditCourseId] = useState("");

//     const openAudit = async (courseId) => {
//         setAuditOpen(true);
//         setAuditCourseId(courseId);
//         const res = await fetch(
//             `${BASE_URL}/courses/audits/by-course?courseId=${encodeURIComponent(
//                 courseId
//             )}`,
//             { headers: authHeaders }
//         );
//         const data = await res.json();
//         setAuditRows(Array.isArray(data) ? data : []);
//     };

//     /* ================= UI ================= */
//     return (
//         <div className="cmp-page">
//             <h1>Course Management</h1>

//             {msg.text && (
//                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`}>
//                     {msg.text}
//                 </div>
//             )}

//             {/* Upload */}
//             <div className="card grid-3">
//                 <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} />
//                 <button className="btn btn-primary" onClick={uploadExcel}>
//                     {uploading ? "Uploading..." : "Upload Excel"}
//                 </button>
//                 <button className="btn" onClick={exportExcel}>Export Excel</button>
//             </div>

//             {/* Table */}
//             <div className="card">
//                 <div className="table-actions">
//                     <input
//                         className="search"
//                         placeholder="Search course..."
//                         value={search}
//                         onChange={(e) => setSearch(e.target.value)}
//                     />
//                 </div>

//                 {/* <table>
//                     <thead>
//                         <tr>
//                             <th>Course ID</th>
//                             <th>Name</th>
//                             <th>Department</th>
//                             <th>Project</th>
//                             <th>Actions</th>
//                         </tr>
//                     </thead>
//                     <tbody>
//                         {filtered.map((c) => (
//                             <tr key={c.courseId}>
//                                 <td>{c.courseId}</td>
//                                 <td>{c.courseName}</td>
//                                 <td>{c.departmentName}</td>
//                                 <td>{c.projectName}</td>
//                                 <td className="actions">
//                                     <button className="btn btn-sm" onClick={() => openEdit(c)}>Edit</button>
//                                     <button className="btn btn-sm" onClick={() => openAudit(c.courseId)}>Audit</button>
//                                     <button className="btn btn-sm btn-danger" onClick={() => deleteCourse(c.courseId)}>
//                                         Deactivate
//                                     </button>
//                                 </td>
//                             </tr>
//                         ))}
//                         {!filtered.length && (
//                             <tr>
//                                 <td colSpan="5" className="muted">
//                                     {loading ? "Loading..." : "No courses found"}
//                                 </td>
//                             </tr>
//                         )}
//                     </tbody>
//                 </table> */}


//                 <div className="table-wrap">
//                     <table className="ep-table">
//                         <thead>
//                             <tr>
//                                 <th>Course ID</th>
//                                 <th>Department</th>
//                                 <th>Project Name</th>
//                                 <th>Schemes / Programs</th>
//                                 <th>Sub Scheme</th>
//                                 <th>Course Type</th>
//                                 <th>Sector</th>
//                                 <th>Job Role Mapping</th>
//                                 <th>QP Code</th>
//                                 <th>Course Name</th>
//                                 <th>NSQF Level</th>
//                                 <th>Min Age</th>
//                                 <th>Min Education</th>
//                                 <th>Duration (Hours)</th>
//                                 <th>Hours / Day</th>
//                                 <th>Course Fee</th>
//                                 <th>Validity</th>
//                                 <th>Actions</th>
//                             </tr>
//                         </thead>

//                         <tbody>
//                             {filtered.map((c) => (
//                                 <tr key={c.courseId}>
//                                     <td>{c.courseId}</td>
//                                     <td>{c.departmentName}</td>
//                                     <td>{c.projectName}</td>
//                                     <td>{c.schemesProgramsName}</td>
//                                     <td>{c.subSchemeName}</td>
//                                     <td>{c.courseType}</td>
//                                     <td>{c.sector}</td>
//                                     <td>{c.jobRoleMappingType}</td>
//                                     <td>{c.qpCode}</td>
//                                     <td>{c.courseName}</td>
//                                     <td>{c.nsqfLevel}</td>
//                                     <td>{c.minimumAge}</td>
//                                     <td>{c.minimumEducationRequired}</td>
//                                     <td>{c.courseDurationHours}</td>
//                                     <td>{c.hoursPerDay}</td>
//                                     <td>{c.courseFee}</td>
//                                     <td>{c.courseValidity}</td>

//                                     <td className="actions">
//                                         <button className="btn btn-sm" onClick={() => openEdit(c)}>
//                                             Edit
//                                         </button>
//                                         <button className="btn btn-sm" onClick={() => openAudit(c.courseId)}>
//                                             Audit
//                                         </button>
//                                         <button
//                                             className="btn btn-sm btn-danger"
//                                             onClick={() => deleteCourse(c.courseId)}
//                                         >
//                                             Deactivate
//                                         </button>
//                                     </td>
//                                 </tr>
//                             ))}

//                             {!filtered.length && (
//                                 <tr>
//                                     <td colSpan="18" className="muted">
//                                         {loading ? "Loading..." : "No courses found"}
//                                     </td>
//                                 </tr>
//                             )}
//                         </tbody>
//                     </table>
//                 </div>

//             </div>

//             {/* EDIT MODAL */}
//             {editingId &&
//                 createPortal(
//                     <div className="modal-overlay" onClick={() => setEditingId(null)}>
//                         <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
//                             <h2>Edit Course</h2>

//                             {Object.keys(emptyForm).map((k) => (
//                                 <input
//                                     key={k}
//                                     placeholder={k}
//                                     value={form[k]}
//                                     onChange={(e) =>
//                                         setForm((p) => ({ ...p, [k]: e.target.value }))
//                                     }
//                                 />
//                             ))}

//                             <div className="modal-actions">
//                                 <button className="btn" onClick={() => setEditingId(null)}>Cancel</button>
//                                 <button className="btn btn-primary" onClick={updateCourse}>Save</button>
//                             </div>
//                         </div>
//                     </div>,
//                     document.body
//                 )}

//             {/* AUDIT MODAL */}
//             {auditOpen &&
//                 createPortal(
//                     <div className="modal-overlay" onClick={() => setAuditOpen(false)}>
//                         <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
//                             <h2>Course Audit Logs</h2>
//                             <pre>{JSON.stringify(auditRows, null, 2)}</pre>
//                         </div>
//                     </div>,
//                     document.body
//                 )}

//             {/* INLINE CSS */}
//             <style>{`
//             /* ===== TABLE SCROLL FIX ===== */
// .table-wrap {
//   width: 100%;
//   overflow-x: auto;
//   overflow-y: hidden;
//   border-radius: 12px;
// }

// /* smooth scrolling on touch devices */
// .table-wrap {
//   -webkit-overflow-scrolling: touch;
// }

// /* force table to respect width */
// .ep-table {
//   width: max-content;        /* KEY FIX */
//   min-width: 1400px;         /* ensures horizontal scroll */
//   border-collapse: collapse;
// }

// /* header */
// .ep-table th {
//   position: sticky;
//   top: 0;
//   background: #fcfcff;
//   z-index: 1;
// }

// /* cells */
// .ep-table th,
// .ep-table td {
//   padding: 10px 12px;
//   border-bottom: 1px solid #e5e7eb;
//   font-size: 13px;
//   white-space: nowrap;       /* IMPORTANT */
// }

// /* actions column */
// .ep-table td.actions {
//   position: sticky;
//   right: 0;
//   background: #fff;
//   box-shadow: -6px 0 8px rgba(0,0,0,0.04);
// }

// /* header for actions */
// .ep-table th.actions {
//   position: sticky;
//   right: 0;
//   background: #fcfcff;
// }

// /* mobile safety */
// @media (max-width: 768px) {
//   .ep-table {
//     min-width: 1600px;
//   }
// }

//         .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
//         h1 { font-size:26px; font-weight:900 }

//         .flash { padding:10px; border-radius:10px; margin:10px 0; font-weight:700 }
//         .flash-ok { background:#ecfdf5; color:#065f46 }
//         .flash-err { background:#fef2f2; color:#991b1b }

//         .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
//         .grid-3 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

//         .btn { border:1px solid #e5e7eb; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer; background:#fff }
//         .btn-primary { background:#2563eb; color:#fff; border-color:#2563eb }
//         .btn-sm { padding:6px 10px; font-size:12px }
//         .btn-danger { color:#991b1b; border-color:#fca5a5 }

//         table { width:100%; border-collapse:collapse }
//         th, td { padding:10px; border-bottom:1px solid #e5e7eb; font-size:13px }
//         th { color:#6b7280; text-align:left }

//         .table-actions { margin-bottom:10px }
//         .search { padding:10px; border-radius:12px; border:1px solid #e5e7eb }

//         .actions { display:flex; gap:8px }
//         .muted { color:#6b7280; font-weight:700 }

//         .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
//         .ep-modal { background:#fff; padding:18px; border-radius:16px; width:520px; max-height:90vh; overflow:auto }
//         .ep-modal.wide { width:900px }
//         .ep-modal input { width:100%; margin-bottom:8px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

//         .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px }
//       `}</style>
//         </div>
//     );
// }



// import React, { useEffect, useMemo, useState } from "react";
// import { createPortal } from "react-dom";
// import * as XLSX from "xlsx";
// import { filterRowsForCurrentScope, getCenterScopedUrl } from "../../utils/centerScope";
// import useDataStore from "../../store/dataStore";

// const BASE_URL = "https://mis.empowerplus.in";

// /* ===== COLUMN DEFINITION (SINGLE SOURCE OF TRUTH) ===== */
// const COURSE_FIELDS = [
//     { key: "courseId", label: "Course ID", readonly: true },
//     // { key: "subSchemeName", label: "Sub Scheme Name" },
//     { key: "courseType", label: "Course Type" },
//     { key: "sector", label: "Sector" },
//     { key: "jobRoleMappingType", label: "Job Role Mapping Type" },
//     { key: "qpCode", label: "QP Code" },
//     { key: "courseName", label: "Course Name" },
//     { key: "nsqfLevel", label: "NSQF Level" },
//     { key: "minimumAge", label: "Minimum Age" },
//     { key: "minimumEducationRequired", label: "Minimum Education Required" },
//     { key: "courseDurationHours", label: "Course Duration (Hours)" },
//     { key: "hoursPerDay", label: "Hours Per Day" },
//     // { key: "courseFee", label: "Course Fee" },
//     { key: "courseValidity", label: "Course Validity" },
//     // { key: "departmentName", label: "Department" },
//     // { key: "projectName", label: "Project Name" },
//     // { key: "schemesProgramsName", label: "Schemes / Programs" },
// ];

// export default function CourseManagementPage() {
//     const token = localStorage.getItem("token");
//     const store = useDataStore();
//     const CACHE_KEY = "courses";

//     /* ================= COMMON ================= */
//     const [msg, setMsg] = useState({ type: "", text: "" });
//     const flash = (type, text) => {
//         setMsg({ type, text });
//     };

//     const authHeaders = useMemo(
//         () => ({ Authorization: `Bearer ${token}` }),
//         [token]
//     );

//     const jsonHeaders = useMemo(
//         () => ({
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//         }),
//         [token]
//     );

//     /* ================= EXCEL UPLOAD ================= */
//     const [file, setFile] = useState(null);
//     const [uploading, setUploading] = useState(false);
//     const [uploadProgress, setUploadProgress] = useState(null);

//     const CHUNK_SIZE = 5000;
//     const PARALLEL = 6;

//     const uploadExcel = async () => {
//         if (!file) return flash("err", "Please select Excel file");
//         let dataRows;
//         try {
//             const buffer = await file.arrayBuffer();
//             const wb = XLSX.read(buffer, { type: "array" });
//             const ws = wb.Sheets[wb.SheetNames[0]];
//             const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
//             dataRows = raw.slice(1);
//         } catch (err) {
//             return flash("err", `Cannot read file: ${err.message}`);
//         }
//         if (!dataRows.length) return flash("err", "Excel file is empty");
//         const totalRows = dataRows.length;
//         const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
//         setUploading(true);
//         setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
//         let uploadedSoFar = 0;
//         let totalInserted = 0;
//         let totalSkipped = 0;
//         let failed = false;
//         let firstError = "";
//         const uploadOneChunk = async (i) => {
//             const chunkRows = dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
//             const res = await fetch(`${BASE_URL}/api/courses/upload-json?actor=Rahul`, {
//                 method: "POST",
//                 headers: { ...authHeaders, "Content-Type": "application/json" },
//                 body: JSON.stringify(chunkRows),
//             });
//             if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
//             const text = await res.text();
//             let data;
//             try { data = JSON.parse(text); } catch { data = text; }
//             if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
//             return { inserted: data?.inserted ?? 0, skipped: data?.skipped ?? 0 };
//         };
//         for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
//             const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
//             const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
//             const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
//             for (const result of results) {
//                 if (result.status === "fulfilled") {
//                     totalInserted += result.value.inserted;
//                     totalSkipped += result.value.skipped;
//                     uploadedSoFar += result.value.inserted + result.value.skipped;
//                 } else {
//                     failed = true;
//                     firstError = result.reason?.message || "Upload failed";
//                 }
//             }
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
//         }
//         setUploading(false);
//         if (!failed) {
//             flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
//             setFile(null);
//             setUploadProgress(null);
//             store.invalidate(CACHE_KEY);
//             fetchAllCourses();
//         } else {
//             flash("err", firstError);
//             setUploadProgress(prev => prev ? { ...prev, error: true } : null);
//         }
//     };

//     /* ================= TABLE ================= */
//     const [courses, setCourses] = useState(() => store.getRows(CACHE_KEY));
//     const [loading, setLoading] = useState(false);
//     const [loadingAll, setLoadingAll] = useState(false);
//     const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
//     const [totalCount, setTotalCount] = useState(null);
//     const [search, setSearch] = useState("");
//     const [visibleCount, setVisibleCount] = useState(100);

//     const fetchFirst100 = async () => {
//         if (store.isCached(CACHE_KEY)) {
//             setCourses(store.getRows(CACHE_KEY));
//             setAllLoaded(store.isAllLoaded(CACHE_KEY));
//             return;
//         }
//         setLoading(true);
//         try {
//             const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/courses"), { headers: authHeaders });
//             const data = await res.json();
//             const all = Array.isArray(data) ? data : [];
//             setCourses(all);
//             setAllLoaded(true);
//             store.setRows(CACHE_KEY, all, true);
//         } catch {
//             setCourses([]);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const fetchAllCourses = async () => {
//         setLoadingAll(true);
//         try {
//             const res = await fetch(getCenterScopedUrl(BASE_URL, "/api/courses"), { headers: authHeaders });
//             const data = await res.json();
//             const all = Array.isArray(data) ? data : [];
//             setCourses(all);
//             setAllLoaded(true);
//             setVisibleCount(all.length);
//             store.markAllLoaded(CACHE_KEY, all);
//         } catch {
//         } finally {
//             setLoadingAll(false);
//         }
//     };

//     useEffect(() => {
//         fetchFirst100();
//     }, []);

//     useEffect(() => {
//         fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
//             .then(r => r.ok ? r.json() : null)
//             .then(d => { if (d) setTotalCount(d.courses ?? null); })
//             .catch(() => {});
//     }, []);

//     const filtered = useMemo(() => {
//         const q = search.toLowerCase();
//         return filterRowsForCurrentScope(courses).filter((c) =>
//             COURSE_FIELDS.some((f) =>
//                 String(c[f.key] ?? "").toLowerCase().includes(q)
//             )
//         );
//     }, [courses, search]);

//     const [exporting, setExporting] = useState(false);

//     const exportExcel = () => {
//         if (exporting) return;
//         setExporting(true);
//         setTimeout(() => {
//             try {
//                 const ws = XLSX.utils.json_to_sheet(filtered);
//                 const wb = XLSX.utils.book_new();
//                 XLSX.utils.book_append_sheet(wb, ws, "Courses");
//                 XLSX.writeFile(wb, "courses.xlsx");
//             } finally {
//                 setExporting(false);
//             }
//         }, 50);
//     };

//     /* ================= EDIT ================= */
//     const emptyForm = useMemo(() => {
//         const o = {};
//         COURSE_FIELDS.forEach((f) => (o[f.key] = ""));
//         return o;
//     }, []);

//     const [editingId, setEditingId] = useState(null);
//     const [form, setForm] = useState(emptyForm);

//     const openEdit = (row) => {
//         setEditingId(row.courseId);
//         const filled = {};
//         COURSE_FIELDS.forEach((f) => {
//             filled[f.key] = row[f.key] ?? "";
//         });
//         setForm(filled);
//     };

//     const updateCourse = async () => {
//         try {
//             const payload = { ...form };
//             delete payload.courseId;

//             await fetch(
//                 `${BASE_URL}/api/courses?courseId=${encodeURIComponent(editingId)}`,
//                 {
//                     method: "PUT",
//                     headers: jsonHeaders,
//                     body: JSON.stringify(payload),
//                 }
//             );
//             flash("ok", "Course updated");
//             setEditingId(null);
//             store.invalidate(CACHE_KEY);
//             fetchAllCourses();
//         } catch {
//             flash("err", "Update failed");
//         }
//     };

//     /* ================= DELETE ================= */
//     const deleteCourse = async (courseId) => {
//         if (!window.confirm("Deactivate this course?")) return;

//         await fetch(
//             `${BASE_URL}/api/courses?courseId=${encodeURIComponent(courseId)}`,
//             {
//                 method: "DELETE",
//                 headers: authHeaders,
//             }
//         );
//         flash("ok", "Course deactivated");
//         store.invalidate(CACHE_KEY);
//         fetchAllCourses();
//     };

//     /* ================= AUDIT ================= */
//     const [auditOpen, setAuditOpen] = useState(false);
//     const [auditRows, setAuditRows] = useState([]);

//     const openAudit = async (courseId) => {
//         setAuditOpen(true);
//         const res = await fetch(
//             `${BASE_URL}/api/courses/audits/by-course?courseId=${encodeURIComponent(
//                 courseId
//             )}`,
//             { headers: authHeaders }
//         );
//         const data = await res.json();
//         setAuditRows(Array.isArray(data) ? data : []);
//     };


//     const downloadTemplate = () => {
//         const a = document.createElement("a");
//         a.href = "/templates/course_creation_template.xlsx"; // local path
//         a.download = "course_creation_template.xlsx";
//         document.body.appendChild(a);
//         a.click();
//         a.remove();
//     };

//     /* ================= UI ================= */
//     return (
//         <div className="cmp-page">
//             <h1>Course Management</h1>

//             {msg.text && (
//                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
//                     <span>{msg.text}</span>
//                     <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
//                 </div>
//             )}

//             {uploadProgress && (
//                 <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
//                     <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
//                         <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
//                             {uploadProgress.error
//                                 ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
//                                 : uploading
//                                     ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
//                                     : `Upload complete`}
//                         </span>
//                         <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
//                             {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
//                             &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
//                         </span>
//                     </div>
//                     <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
//                         <div style={{
//                             height: "100%",
//                             width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
//                             background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
//                             borderRadius: 999,
//                             transition: "width 0.3s ease",
//                         }} />
//                     </div>
//                     <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
//                         {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
//                     </div>
//                 </div>
//             )}

//             {/* Upload */}
//             <div className="card grid-3">
//                 <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }} />
//                 <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
//                     {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
//                 </button>
//                 {/* <button className="btn" onClick={exportExcel}>Export Excel</button> */}
//                 <button className="btn btn-outline" onClick={downloadTemplate}>
//                     Download Excel Template
//                 </button>
//             </div>

//             {/* Table */}
//             <div className="card">

//                 <div className="card-top">
//                     <div className="card-title" style={{ fontWeight: "800" }}>2️⃣ Course Table</div>

//                     <div className="table-actions" style={{ marginTop: "1rem" }}>
//                         <input
//                             className="search"
//                             placeholder="Search..."
//                             style={{ marginRight: "1rem" }}
//                             value={search}
//                             onChange={(e) => setSearch(e.target.value)}
//                         />
//                         <button className="btn" onClick={exportExcel} disabled={exporting}>{exporting ? "Processing..." : "Export Excel"}</button>
//                     </div>
//                 </div>
//                 {totalCount !== null && (
//                     <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
//                         Total: {totalCount.toLocaleString()}
//                     </div>
//                 )}




//                 <div className="table-wrap">
//                     <table className="ep-table">
//                         <thead>
//                             <tr>
//                                 <th>#</th>
//                                 {COURSE_FIELDS.map((f) => (
//                                     <th key={f.key}>{f.label}</th>
//                                 ))}
//                                 <th>Actions</th>
//                             </tr>
//                         </thead>

//                         <tbody>
//                             {filtered.slice(0, visibleCount).map((c, i) => (
//                                 <tr key={c.courseId}>
//                                     <td>{i + 1}</td>
//                                     {COURSE_FIELDS.map((f) => (
//                                         <td key={f.key}>{c[f.key]}</td>
//                                     ))}
//                                     <td className="actions">
//                                         {/* <button className="btn btn-sm" onClick={() => openEdit(c)}>Edit</button> */}
//                                         {/* <button className="btn btn-sm" onClick={() => openAudit(c.courseId)}>Audit</button> */}
//                                         <button className="btn btn-sm btn-danger" onClick={() => deleteCourse(c.courseId)}>Deactivate</button>
//                                     </td>
//                                 </tr>
//                             ))}

//                             {!filtered.length && (
//                                 <tr>
//                                     <td colSpan={COURSE_FIELDS.length + 2} className="muted">
//                                         {loading ? "Loading..." : "No courses found"}
//                                     </td>
//                                 </tr>
//                             )}
//                             {!allLoaded && !loading && (
//                                 <tr>
//                                     <td colSpan={COURSE_FIELDS.length + 2} style={{ textAlign: "center", padding: "14px" }}>
//                                         <button className="btn btn-outline" onClick={fetchAllCourses} disabled={loadingAll}>
//                                             {loadingAll ? "Loading all data…" : "Load All Data"}
//                                         </button>
//                                     </td>
//                                 </tr>
//                             )}
//                             {allLoaded && filtered.length > visibleCount && (
//                                 <tr>
//                                     <td colSpan={COURSE_FIELDS.length + 2} style={{ textAlign: "center", padding: "14px" }}>
//                                         <button className="btn btn-outline" onClick={() => setVisibleCount(c => c + 100)}>
//                                             Show More ({filtered.length - visibleCount} remaining)
//                                         </button>
//                                     </td>
//                                 </tr>
//                             )}
//                         </tbody>
//                     </table>
//                 </div>
//             </div>

//             {/* EDIT MODAL */}
//             {editingId &&
//                 createPortal(
//                     <div className="modal-overlay" onClick={() => setEditingId(null)}>
//                         <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
//                             <h2>Edit Course</h2>

//                             {COURSE_FIELDS.map(
//                                 (f) =>
//                                     !f.readonly && (
//                                         <input
//                                             key={f.key}
//                                             placeholder={f.label}
//                                             value={form[f.key]}
//                                             onChange={(e) =>
//                                                 setForm((p) => ({ ...p, [f.key]: e.target.value }))
//                                             }
//                                         />
//                                     )
//                             )}

//                             <div className="modal-actions">
//                                 <button className="btn" onClick={() => setEditingId(null)}>Cancel</button>
//                                 <button className="btn btn-primary" onClick={updateCourse}>Save</button>
//                             </div>
//                         </div>
//                     </div>,
//                     document.body
//                 )}

//             {/* AUDIT MODAL */}
//             {auditOpen &&
//                 createPortal(
//                     <div className="modal-overlay" onClick={() => setAuditOpen(false)}>
//                         <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
//                             <h2>Course Audit Logs</h2>
//                             <pre>{JSON.stringify(auditRows, null, 2)}</pre>
//                         </div>
//                     </div>,
//                     document.body
//                 )}

//             {/* INLINE CSS */}
//             <style>{`
//         .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
//         h1 { font-size:26px; font-weight:900 }

//         .flash { padding:10px; border-radius:10px; margin:10px 0; font-weight:700 }
//         .flash-ok { background:#ecfdf5; color:#065f46 }
//         .flash-err { background:#fef2f2; color:#991b1b }

//         .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
//         .grid-3 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

//         .btn { border:1px solid #e5e7eb; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer; background:#fff }
//         .btn-primary { background:#2563eb; color:#fff; border-color:#2563eb }
//         .btn-sm { padding:6px 10px; font-size:12px }
//         .btn-danger { color:#991b1b; border-color:#fca5a5 }

//         .search { margin-bottom:10px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

//         .table-wrap { width:100%; overflow-x:auto }
//         .ep-table { width:max-content; min-width:1600px; border-collapse:collapse }
//         th, td { padding:10px 12px; border-bottom:1px solid #e5e7eb; font-size:13px; white-space:nowrap }
//         th { background:#fcfcff; color:#6b7280; text-align:left }

//         .actions { display:flex; gap:6px }
//         .muted { color:#6b7280; font-weight:700 }

//         .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
//         .ep-modal { background:#fff; padding:18px; border-radius:16px; width:600px; max-height:90vh; overflow:auto }
//         .ep-modal.wide { width:900px }
//         .ep-modal input { width:100%; margin-bottom:8px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

//         .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px }
//       `}</style>
//         </div>
//     );
// }




// // import React, { useEffect, useMemo, useState } from "react";
// // import { createPortal } from "react-dom";
// // import * as XLSX from "xlsx";

// // const BASE_URL = "https://mis.empowerplus.in";

// // export default function CourseManagementPage() {
// //     const token = localStorage.getItem("token");

// //     /* ================= COMMON ================= */
// //     const [msg, setMsg] = useState({ type: "", text: "" });
// //     const flash = (type, text) => {
// //         setMsg({ type, text });
// //         setTimeout(() => setMsg({ type: "", text: "" }), 3000);
// //     };

// //     const authHeaders = useMemo(
// //         () => ({ Authorization: `Bearer ${token}` }),
// //         [token]
// //     );

// //     const jsonHeaders = useMemo(
// //         () => ({
// //             Authorization: `Bearer ${token}`,
// //             "Content-Type": "application/json",
// //         }),
// //         [token]
// //     );

// //     /* ================= EXCEL UPLOAD ================= */
// //     const [file, setFile] = useState(null);
// //     const [uploading, setUploading] = useState(false);

// //     const uploadExcel = async () => {
// //         if (!file) return flash("err", "Please select Excel file");

// //         const fd = new FormData();
// //         fd.append("file", file);

// //         setUploading(true);
// //         try {
// //             await fetch(`${BASE_URL}/api/courses/upload?actor=Rahul`, {
// //                 method: "POST",
// //                 headers: authHeaders,
// //                 body: fd,
// //             });
// //             flash("ok", "Excel uploaded successfully");
// //             setFile(null);
// //             fetchCourses();
// //         } catch {
// //             flash("err", "Upload failed");
// //         } finally {
// //             setUploading(false);
// //         }
// //     };

// //     /* ================= TABLE ================= */
// //     const [courses, setCourses] = useState([]);
// //     const [loading, setLoading] = useState(false);
// //     const [search, setSearch] = useState("");

// //     const fetchCourses = async () => {
// //         setLoading(true);
// //         try {
// //             const res = await fetch(`${BASE_URL}/api/courses`, {
// //                 headers: authHeaders,
// //             });
// //             const data = await res.json();
// //             setCourses(Array.isArray(data) ? data : []);
// //         } catch {
// //             setCourses([]);
// //         } finally {
// //             setLoading(false);
// //         }
// //     };

// //     useEffect(() => {
// //         fetchCourses();
// //     }, []);

// //     const filtered = useMemo(() => {
// //         const q = search.toLowerCase();
// //         return courses.filter(
// //             (c) =>
// //                 c.courseId?.toLowerCase().includes(q) ||
// //                 c.courseName?.toLowerCase().includes(q) ||
// //                 c.projectName?.toLowerCase().includes(q) ||
// //                 c.departmentName?.toLowerCase().includes(q)
// //         );
// //     }, [courses, search]);

// //     const exportExcel = () => {
// //         const ws = XLSX.utils.json_to_sheet(filtered);
// //         const wb = XLSX.utils.book_new();
// //         XLSX.utils.book_append_sheet(wb, ws, "Courses");
// //         XLSX.writeFile(wb, "courses.xlsx");
// //     };

// //     /* ================= EDIT ================= */
// //     const emptyForm = {
// //         courseName: "",
// //         departmentName: "",
// //         projectName: "",
// //         sector: "",
// //         qpCode: "",
// //         nsqfLevel: "",
// //         courseDurationHours: "",
// //     };

// //     const [editingId, setEditingId] = useState(null);
// //     const [form, setForm] = useState(emptyForm);

// //     const openEdit = (row) => {
// //         setEditingId(row.courseId);
// //         setForm({
// //             courseName: row.courseName ?? "",
// //             departmentName: row.departmentName ?? "",
// //             projectName: row.projectName ?? "",
// //             sector: row.sector ?? "",
// //             qpCode: row.qpCode ?? "",
// //             nsqfLevel: row.nsqfLevel ?? "",
// //             courseDurationHours: row.courseDurationHours ?? "",
// //         });
// //     };

// //     const updateCourse = async () => {
// //         try {
// //             await fetch(
// //                 `${BASE_URL}/courses?courseId=${encodeURIComponent(editingId)}`,
// //                 {
// //                     method: "PUT",
// //                     headers: jsonHeaders,
// //                     body: JSON.stringify(form),
// //                 }
// //             );
// //             flash("ok", "Course updated");
// //             setEditingId(null);
// //             fetchCourses();
// //         } catch {
// //             flash("err", "Update failed");
// //         }
// //     };

// //     /* ================= DELETE ================= */
// //     const deleteCourse = async (courseId) => {
// //         if (!window.confirm("Deactivate this course?")) return;

// //         await fetch(
// //             `${BASE_URL}/courses?courseId=${encodeURIComponent(courseId)}`,
// //             {
// //                 method: "DELETE",
// //                 headers: authHeaders,
// //             }
// //         );
// //         flash("ok", "Course deactivated");
// //         fetchCourses();
// //     };

// //     /* ================= AUDIT ================= */
// //     const [auditOpen, setAuditOpen] = useState(false);
// //     const [auditRows, setAuditRows] = useState([]);
// //     const [auditCourseId, setAuditCourseId] = useState("");

// //     const openAudit = async (courseId) => {
// //         setAuditOpen(true);
// //         setAuditCourseId(courseId);
// //         const res = await fetch(
// //             `${BASE_URL}/courses/audits/by-course?courseId=${encodeURIComponent(
// //                 courseId
// //             )}`,
// //             { headers: authHeaders }
// //         );
// //         const data = await res.json();
// //         setAuditRows(Array.isArray(data) ? data : []);
// //     };

// //     /* ================= UI ================= */
// //     return (
// //         <div className="cmp-page">
// //             <h1>Course Management</h1>

// //             {msg.text && (
// //                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`}>
// //                     {msg.text}
// //                 </div>
// //             )}

// //             {/* Upload */}
// //             <div className="card grid-3">
// //                 <input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} />
// //                 <button className="btn btn-primary" onClick={uploadExcel}>
// //                     {uploading ? "Uploading..." : "Upload Excel"}
// //                 </button>
// //                 <button className="btn" onClick={exportExcel}>Export Excel</button>
// //             </div>

// //             {/* Table */}
// //             <div className="card">
// //                 <div className="table-actions">
// //                     <input
// //                         className="search"
// //                         placeholder="Search course..."
// //                         value={search}
// //                         onChange={(e) => setSearch(e.target.value)}
// //                     />
// //                 </div>

// //                 {/* <table>
// //                     <thead>
// //                         <tr>
// //                             <th>Course ID</th>
// //                             <th>Name</th>
// //                             <th>Department</th>
// //                             <th>Project</th>
// //                             <th>Actions</th>
// //                         </tr>
// //                     </thead>
// //                     <tbody>
// //                         {filtered.map((c) => (
// //                             <tr key={c.courseId}>
// //                                 <td>{c.courseId}</td>
// //                                 <td>{c.courseName}</td>
// //                                 <td>{c.departmentName}</td>
// //                                 <td>{c.projectName}</td>
// //                                 <td className="actions">
// //                                     <button className="btn btn-sm" onClick={() => openEdit(c)}>Edit</button>
// //                                     <button className="btn btn-sm" onClick={() => openAudit(c.courseId)}>Audit</button>
// //                                     <button className="btn btn-sm btn-danger" onClick={() => deleteCourse(c.courseId)}>
// //                                         Deactivate
// //                                     </button>
// //                                 </td>
// //                             </tr>
// //                         ))}
// //                         {!filtered.length && (
// //                             <tr>
// //                                 <td colSpan="5" className="muted">
// //                                     {loading ? "Loading..." : "No courses found"}
// //                                 </td>
// //                             </tr>
// //                         )}
// //                     </tbody>
// //                 </table> */}


// //                 <div className="table-wrap">
// //                     <table className="ep-table">
// //                         <thead>
// //                             <tr>
// //                                 <th>Course ID</th>
// //                                 <th>Department</th>
// //                                 <th>Project Name</th>
// //                                 <th>Schemes / Programs</th>
// //                                 <th>Sub Scheme</th>
// //                                 <th>Course Type</th>
// //                                 <th>Sector</th>
// //                                 <th>Job Role Mapping</th>
// //                                 <th>QP Code</th>
// //                                 <th>Course Name</th>
// //                                 <th>NSQF Level</th>
// //                                 <th>Min Age</th>
// //                                 <th>Min Education</th>
// //                                 <th>Duration (Hours)</th>
// //                                 <th>Hours / Day</th>
// //                                 <th>Course Fee</th>
// //                                 <th>Validity</th>
// //                                 <th>Actions</th>
// //                             </tr>
// //                         </thead>

// //                         <tbody>
// //                             {filtered.map((c) => (
// //                                 <tr key={c.courseId}>
// //                                     <td>{c.courseId}</td>
// //                                     <td>{c.departmentName}</td>
// //                                     <td>{c.projectName}</td>
// //                                     <td>{c.schemesProgramsName}</td>
// //                                     <td>{c.subSchemeName}</td>
// //                                     <td>{c.courseType}</td>
// //                                     <td>{c.sector}</td>
// //                                     <td>{c.jobRoleMappingType}</td>
// //                                     <td>{c.qpCode}</td>
// //                                     <td>{c.courseName}</td>
// //                                     <td>{c.nsqfLevel}</td>
// //                                     <td>{c.minimumAge}</td>
// //                                     <td>{c.minimumEducationRequired}</td>
// //                                     <td>{c.courseDurationHours}</td>
// //                                     <td>{c.hoursPerDay}</td>
// //                                     <td>{c.courseFee}</td>
// //                                     <td>{c.courseValidity}</td>

// //                                     <td className="actions">
// //                                         <button className="btn btn-sm" onClick={() => openEdit(c)}>
// //                                             Edit
// //                                         </button>
// //                                         <button className="btn btn-sm" onClick={() => openAudit(c.courseId)}>
// //                                             Audit
// //                                         </button>
// //                                         <button
// //                                             className="btn btn-sm btn-danger"
// //                                             onClick={() => deleteCourse(c.courseId)}
// //                                         >
// //                                             Deactivate
// //                                         </button>
// //                                     </td>
// //                                 </tr>
// //                             ))}

// //                             {!filtered.length && (
// //                                 <tr>
// //                                     <td colSpan="18" className="muted">
// //                                         {loading ? "Loading..." : "No courses found"}
// //                                     </td>
// //                                 </tr>
// //                             )}
// //                         </tbody>
// //                     </table>
// //                 </div>

// //             </div>

// //             {/* EDIT MODAL */}
// //             {editingId &&
// //                 createPortal(
// //                     <div className="modal-overlay" onClick={() => setEditingId(null)}>
// //                         <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
// //                             <h2>Edit Course</h2>

// //                             {Object.keys(emptyForm).map((k) => (
// //                                 <input
// //                                     key={k}
// //                                     placeholder={k}
// //                                     value={form[k]}
// //                                     onChange={(e) =>
// //                                         setForm((p) => ({ ...p, [k]: e.target.value }))
// //                                     }
// //                                 />
// //                             ))}

// //                             <div className="modal-actions">
// //                                 <button className="btn" onClick={() => setEditingId(null)}>Cancel</button>
// //                                 <button className="btn btn-primary" onClick={updateCourse}>Save</button>
// //                             </div>
// //                         </div>
// //                     </div>,
// //                     document.body
// //                 )}

// //             {/* AUDIT MODAL */}
// //             {auditOpen &&
// //                 createPortal(
// //                     <div className="modal-overlay" onClick={() => setAuditOpen(false)}>
// //                         <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
// //                             <h2>Course Audit Logs</h2>
// //                             <pre>{JSON.stringify(auditRows, null, 2)}</pre>
// //                         </div>
// //                     </div>,
// //                     document.body
// //                 )}

// //             {/* INLINE CSS */}
// //             <style>{`
// //             /* ===== TABLE SCROLL FIX ===== */
// // .table-wrap {
// //   width: 100%;
// //   overflow-x: auto;
// //   overflow-y: hidden;
// //   border-radius: 12px;
// // }

// // /* smooth scrolling on touch devices */
// // .table-wrap {
// //   -webkit-overflow-scrolling: touch;
// // }

// // /* force table to respect width */
// // .ep-table {
// //   width: max-content;        /* KEY FIX */
// //   min-width: 1400px;         /* ensures horizontal scroll */
// //   border-collapse: collapse;
// // }

// // /* header */
// // .ep-table th {
// //   position: sticky;
// //   top: 0;
// //   background: #fcfcff;
// //   z-index: 1;
// // }

// // /* cells */
// // .ep-table th,
// // .ep-table td {
// //   padding: 10px 12px;
// //   border-bottom: 1px solid #e5e7eb;
// //   font-size: 13px;
// //   white-space: nowrap;       /* IMPORTANT */
// // }

// // /* actions column */
// // .ep-table td.actions {
// //   position: sticky;
// //   right: 0;
// //   background: #fff;
// //   box-shadow: -6px 0 8px rgba(0,0,0,0.04);
// // }

// // /* header for actions */
// // .ep-table th.actions {
// //   position: sticky;
// //   right: 0;
// //   background: #fcfcff;
// // }

// // /* mobile safety */
// // @media (max-width: 768px) {
// //   .ep-table {
// //     min-width: 1600px;
// //   }
// // }

// //         .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
// //         h1 { font-size:26px; font-weight:900 }

// //         .flash { padding:10px; border-radius:10px; margin:10px 0; font-weight:700 }
// //         .flash-ok { background:#ecfdf5; color:#065f46 }
// //         .flash-err { background:#fef2f2; color:#991b1b }

// //         .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
// //         .grid-3 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

// //         .btn { border:1px solid #e5e7eb; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer; background:#fff }
// //         .btn-primary { background:#2563eb; color:#fff; border-color:#2563eb }
// //         .btn-sm { padding:6px 10px; font-size:12px }
// //         .btn-danger { color:#991b1b; border-color:#fca5a5 }

// //         table { width:100%; border-collapse:collapse }
// //         th, td { padding:10px; border-bottom:1px solid #e5e7eb; font-size:13px }
// //         th { color:#6b7280; text-align:left }

// //         .table-actions { margin-bottom:10px }
// //         .search { padding:10px; border-radius:12px; border:1px solid #e5e7eb }

// //         .actions { display:flex; gap:8px }
// //         .muted { color:#6b7280; font-weight:700 }

// //         .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
// //         .ep-modal { background:#fff; padding:18px; border-radius:16px; width:520px; max-height:90vh; overflow:auto }
// //         .ep-modal.wide { width:900px }
// //         .ep-modal input { width:100%; margin-bottom:8px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

// //         .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px }
// //       `}</style>
// //         </div>
// //     );
// // }
